import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { LOGO_GROUPS, type LogoItem, type LogoSize } from '../data/logos';
import { formatBytes } from '@/lib/utils';

const SIZE_ORDER: LogoSize[] = ['favicon', 'small', 'medium', 'large', 'default'];

function LogoCard({ item }: { item: LogoItem }) {
  const initial = item.variants.find((v) => v.size === 'medium') || item.variants[0];
  const [selected, setSelected] = useState(initial);
  const [size, setSize] = useState<number | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSize(null);
    setDims(null);
    fetch(selected.src, { method: 'HEAD' })
      .then((res) => {
        if (cancelled) return;
        const len = res.headers.get('content-length');
        if (len) setSize(parseInt(len, 10));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selected.src]);

  const orderedVariants = [...item.variants].sort(
    (a, b) => SIZE_ORDER.indexOf(a.size) - SIZE_ORDER.indexOf(b.size),
  );

  return (
    <div className="border rounded-lg overflow-hidden bg-white flex flex-col">
      <div
        className={`flex items-center justify-center p-8 min-h-[180px] ${
          item.background === 'dark' ? 'bg-[#1C143D]' : 'bg-[#F0F0F0]'
        }`}
      >
        <img
          key={selected.src}
          src={selected.src}
          alt={item.name}
          className="max-w-full max-h-[120px] object-contain"
          onLoad={(e) => {
            const el = e.currentTarget;
            setDims({ w: el.naturalWidth, h: el.naturalHeight });
          }}
        />
      </div>
      <div className="p-3 border-t flex-1 flex flex-col">
        <div className="text-sm font-medium text-slate-900">{item.name}</div>
        {item.description && (
          <div className="text-xs text-slate-500 mt-0.5">{item.description}</div>
        )}

        <div className="flex flex-wrap gap-1 mt-2">
          {orderedVariants.map((v) => (
            <button
              key={v.size}
              type="button"
              onClick={() => setSelected(v)}
              className={`px-2 py-0.5 text-[10px] rounded border uppercase tracking-wide ${
                selected.size === v.size
                  ? 'bg-[#5A1DEF] text-white border-[#5A1DEF]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}
            >
              {v.size}
            </button>
          ))}
        </div>

        <dl className="grid grid-cols-3 gap-x-2 gap-y-0.5 text-[11px] text-slate-600 mt-2">
          <dt className="text-slate-400">Size</dt>
          <dd className="col-span-2">{size === null ? '—' : formatBytes(size)}</dd>
          <dt className="text-slate-400">Dimensions</dt>
          <dd className="col-span-2">{dims ? `${dims.w} × ${dims.h} px` : '—'}</dd>
        </dl>

        <a
          href={selected.src}
          download
          className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 hover:text-blue-800"
        >
          <Download className="h-3 w-3" />
          Download {selected.size.toUpperCase()}
        </a>
      </div>
    </div>
  );
}

export function LogosTab() {
  return (
    <div className="space-y-8">
      {LOGO_GROUPS.map((group) => (
        <section key={group.title}>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600 mb-3">{group.title}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {group.items.map((item) => (
              <LogoCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      ))}
      <p className="text-xs text-slate-500 pt-2">
        All sizes stored in <code className="px-1 rounded bg-slate-100">apps/frontend-react/public/atek/branding/logos/</code>.
        Click a size pill to switch; download link points to the selected size.
      </p>
    </div>
  );
}
